import * as React from 'react';
import { useSelector } from 'react-redux';
import { lookupsLoadingFinishedSelector } from 'store/lookups/selector';
import useDebouncedState from 'hooks/useDebouncedState';
import { mergeStyleSets } from '@fluentui/react/lib/Styling';
import { PrimaryButton } from '@fluentui/react';
import moment from 'moment-timezone';
import { SelectOption } from 'formComponents/Select/types';
import { ValueType } from 'react-select';
import { Select } from 'formComponents/Select/Select';
import { useBetreuer } from 'api/betreuer';
import { authSelector } from 'store/auth/selector';
import { Label } from '@fluentui/react/lib/Label';
import range from 'lodash/range';
import { Checkbox } from '@fluentui/react/lib/Checkbox';
import { formStyles } from 'styles/formStyles';
import { ConfirmMessage } from 'formComponents/ConfirmMessage/ConfirmMessage';
import { LoadingIndicator } from 'layout/LoadingIndicator/LoadingIndicator';
import { generateReportPrintUrl } from 'reports/utils';

import { Betreuerabrechnung, BetreuerabrechnungHandle } from './Betreuerabrechnung';

const styles = mergeStyleSets({
    container: {
        margin: 0,
        marginTop: 5,
        padding: 0,
        display: 'grid',
        gridTemplateRows: 'max-content 1fr',
        gridRowGap: 10,
        height: 'calc(100vh - 75px)',
    },
    searchLine: {
        display: 'grid',
        gridTemplateColumns: '180px 160px 160px max-content max-content 1fr max-content',
        gridColumnGap: 10,
        marginTop: 10,
    },
    actionButtons: {
        marginTop: 29,
        display: 'grid',
        gridTemplateColumns: 'repeat(2, max-content)',
        gridColumnGap: 10,
    },
    finishMessage: {
        height: 120,
    },
});

interface FilterValues {
    abrJahr: number;
    abrMo: number;
    currentMonth: boolean;
    betreuer: SelectOption;
    showFAnteil: boolean;
}

const defaultFilterValues: FilterValues = {
    abrJahr: moment().year(),
    abrMo: moment().month() + 1,
    currentMonth: false,
    betreuer: { value: '', label: '' },
    showFAnteil: false,
};

const yearOptions = range(moment().year(), moment().year() - 25).map((year) => ({
    value: year.toString(),
    label: year.toString(),
}));

const monthOptions = range(0, 12).map((month) => ({
    value: (month + 1).toString(),
    label: (month + 1).toString(),
}));

export const BetreuerabrechnungView: React.FC = () => {
    const auth = useSelector(authSelector);
    const lookupsLoadingFinished = useSelector(lookupsLoadingFinishedSelector);
    const betreuer = useBetreuer();
    const [filterValues, debouncedFilterValues, setFilterValues] = useDebouncedState<FilterValues>({
        ...defaultFilterValues,
        betreuer: {
            value: auth.nr?.toString() || '',
            label: auth.name || '?',
        },
    });

    const betreuerFilterOptions = React.useMemo<SelectOption[]>(() => {
        const options: SelectOption[] = [];

        if (auth.rolle === 'AD') {
            options.push({
                value: auth.nr?.toString() || '',
                label: auth.name || '?',
            });
        } else {
            betreuer
                .filter((b) => b.aktiv && b.name)
                .sort((a, b) => a.name.localeCompare(b.name))
                .forEach((b) => options.push({ value: b.nr.toString(), label: b.name }));
        }

        return options;
    }, [auth, betreuer]);

    const selectedYearOption = React.useMemo<SelectOption>(
        () => ({ value: filterValues.abrJahr.toString(), label: filterValues.abrJahr.toString() }),
        [filterValues.abrJahr],
    );

    const selectedMonthOption = React.useMemo<SelectOption>(
        () => ({ value: filterValues.abrMo.toString(), label: filterValues.abrMo.toString() }),
        [filterValues.abrMo],
    );

    const betreuerabrechnungRef = React.useRef<BetreuerabrechnungHandle>(null);

    const [showFinish, setShowFinish] = React.useState(false);
    const [finishing, setFinishing] = React.useState(false);
    const [finishResult, setFinishResult] = React.useState<null | string>(null);

    if (!lookupsLoadingFinished) {
        return <div />;
    }

    return (
        <div className={styles.container}>
            <div className={styles.searchLine}>
                <div>
                    <Label>Betreuer</Label>
                    <Select
                        name="betreuer"
                        value={filterValues.betreuer}
                        options={betreuerFilterOptions}
                        onChange={(newValue: ValueType<SelectOption, false>): void => {
                            setFilterValues({
                                ...filterValues,
                                betreuer: newValue || { value: '', label: '' },
                            });
                        }}
                    />
                </div>

                <div>
                    <Label>Jahr</Label>
                    <Select
                        name="Jahr"
                        options={yearOptions}
                        value={selectedYearOption}
                        isDisabled={filterValues.currentMonth}
                        onChange={(newOption) => {
                            setFilterValues({
                                ...filterValues,
                                abrJahr: parseInt(newOption ? newOption.value : moment().year().toString(), 10),
                            });
                        }}
                    />
                </div>
                <div>
                    <Label>Monat</Label>
                    <Select
                        name="Monat"
                        options={monthOptions}
                        value={selectedMonthOption}
                        isDisabled={filterValues.currentMonth}
                        onChange={(newOption) => {
                            setFilterValues({
                                ...filterValues,
                                abrMo: parseInt(newOption ? newOption.value : (moment().month() + 1).toString(), 10),
                            });
                        }}
                    />
                </div>

                <div>
                    <Checkbox
                        className={formStyles.checkboxInsideFormAligned}
                        label="Offen für Abrechnung"
                        checked={filterValues.currentMonth}
                        onChange={(event, checked) => {
                            const month = moment.tz().subtract({ months: 1 });

                            setFilterValues({
                                ...filterValues,
                                abrJahr: month.year(),
                                abrMo: month.month() + 1,
                                currentMonth: checked || false,
                            });
                        }}
                    />
                </div>

                <div>
                    <Checkbox
                        className={formStyles.checkboxInsideFormAligned}
                        label="F-Anteil anzeigen"
                        checked={filterValues.showFAnteil}
                        onChange={(event, checked) => {
                            setFilterValues({
                                ...filterValues,
                                showFAnteil: checked || false,
                            });
                        }}
                    />
                </div>

                <div />

                <div className={styles.actionButtons}>
                    <PrimaryButton
                        text="Abschließen"
                        disabled={!filterValues.currentMonth}
                        onClick={() => {
                            setShowFinish(true);
                        }}
                    />

                    <PrimaryButton
                        onClick={() => {
                            window.open(
                                generateReportPrintUrl('betreuerabrechnung', {
                                    abrJahr: filterValues.abrJahr || 2021,
                                    abrMo: filterValues.abrMo || 12,
                                    betreuer: filterValues.betreuer.value,
                                    currentMonth: filterValues.currentMonth,
                                    showFAnteil: filterValues.showFAnteil,
                                }),
                            );
                        }}
                        text="Drucken"
                    />
                </div>
            </div>

            <Betreuerabrechnung
                ref={betreuerabrechnungRef}
                abrJahr={debouncedFilterValues.abrJahr}
                abrMo={debouncedFilterValues.abrMo}
                currentMonth={debouncedFilterValues.currentMonth}
                betreuer={parseInt(debouncedFilterValues.betreuer.value, 10)}
                showFAnteil={debouncedFilterValues.showFAnteil}
            />

            {showFinish && (
                <ConfirmMessage
                    title="Rechnungen abschließen"
                    content={
                        <div className={styles.finishMessage}>
                            Sollen die Rechnungen für {selectedYearOption.value}/{selectedMonthOption.value} -{' '}
                            {filterValues.betreuer.label} abgeschlossen werden?
                            {finishing && <LoadingIndicator />}
                        </div>
                    }
                    onConfirm={async () => {
                        if (!finishing) {
                            setFinishing(true);
                            const result = await betreuerabrechnungRef.current?.finish();
                            setFinishResult(result || null);
                            setShowFinish(false);
                            setFinishing(false);
                        }
                    }}
                    onCancel={() => {
                        if (!finishing) {
                            setShowFinish(false);
                        }
                    }}
                    confirmDisabled={finishing}
                    cancelText={finishing ? null : 'Nein'}
                />
            )}

            {finishResult && (
                <ConfirmMessage
                    message={finishResult}
                    title="Rechnungen abgeschlossen"
                    onConfirm={() => {
                        setFinishResult(null);
                    }}
                    confirmText="Ok"
                    cancelText={null}
                />
            )}
        </div>
    );
};
